/**
 * TaskTemplateStore — the Task Templates feature's data-access wrapper.
 *
 * Strict layering: this is the ONLY task-templates module that talks to the API,
 * and it does so exclusively through the {@link EngineeringVectorApi} facade
 * (`api.documents<TaskTemplate>('taskTemplates')` + `api.identityProvider`). It
 * never imports ApiClient or calls fetch. React hooks call this store; pages
 * call the hook.
 *
 * Persistence model (mirrors {@link ../preplanning/prePlanStore}): each record is
 * one document with id `tmpl_<uuid>` in the real 'taskTemplates' generic-store
 * collection (DDB pointer row + S3 body). The short discovery fields
 * (name/isDefault + createdBy + timestamps + deleted flag) are mirrored onto the
 * pointer row's index attributes so {@link listSummaries} reconstructs summaries
 * directly from pointer rows WITHOUT hydrating S3 bodies; the Markdown `content`
 * and `sections` live only in the S3 body and are read on demand by {@link get}.
 * Tenant scoping is automatic — DocumentStore routes by the active team bundle.
 *
 * Deletion: the API layer exposes no delete op, so {@link softDelete} sets a
 * `deleted` flag on the document and its pointer row; queries filter it out.
 *
 * NO backend changes are required: this reuses the generic document store.
 */

import type { DocumentStore, EngineeringVectorApi } from '../../api';
import type { TaskTemplate, TaskTemplateDraft, TaskTemplateSummary } from './types';
import {
  TASK_TEMPLATES_COLLECTION,
  TASK_TEMPLATE_ROW,
  defaultTaskTemplateDraft,
  needsDefaultSeed,
  taskTemplateDocId,
  taskTemplateIndexAttributes,
  taskTemplateSummaryFromRow,
} from './taskTemplateModel';

export class TaskTemplateStore {
  private readonly docs: DocumentStore<TaskTemplate>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<TaskTemplate>(TASK_TEMPLATES_COLLECTION);
  }

  /** Lists all non-deleted template summaries (defensively scoped to this collection). */
  async listSummaries(): Promise<TaskTemplateSummary[]> {
    const rows = await this.docs.list();
    return rows
      .filter(
        (r) =>
          r[TASK_TEMPLATE_ROW.collection] === TASK_TEMPLATES_COLLECTION &&
          r[TASK_TEMPLATE_ROW.deleted] !== 'true' &&
          !!r[TASK_TEMPLATE_ROW.primaryIdentifier],
      )
      .map((r) => taskTemplateSummaryFromRow(r));
  }

  /** Loads a single template record by id (hydrates the S3 body). */
  async get(id: string): Promise<TaskTemplate | undefined> {
    return this.docs.get(id);
  }

  /**
   * Registers a new template. Seeds id, createdBy (from identity), createdAt and
   * lastUpdated, persists it, and returns the saved record. When the draft is
   * flagged default, clears the flag on every other template first.
   */
  async create(draft: TaskTemplateDraft): Promise<TaskTemplate> {
    const identity = await this.api.identityProvider.getIdentity();
    const template = this.fromDraft(
      taskTemplateDocId(),
      draft,
      identity.alias,
      undefined,
    );
    if (template.isDefault) await this.clearDefaults();
    await this.docs.save(
      template.id,
      template,
      taskTemplateIndexAttributes(template),
    );
    return template;
  }

  /**
   * Applies a draft's fields to an existing template in place (same id/createdBy/
   * createdAt), refreshes lastUpdated, re-mirrors index attributes, and returns
   * the saved record. When the draft is flagged default, clears the flag on
   * every other template first.
   */
  async applyDraft(
    template: TaskTemplate,
    draft: TaskTemplateDraft,
  ): Promise<TaskTemplate> {
    const updated = this.fromDraft(
      template.id,
      draft,
      template.createdBy,
      template.createdAt,
    );
    if (updated.isDefault) await this.clearDefaults(updated.id);
    await this.docs.update(
      updated.id,
      updated,
      taskTemplateIndexAttributes(updated),
    );
    return updated;
  }

  /** Marks exactly one template as the default (clearing the flag elsewhere). */
  async setDefault(id: string): Promise<void> {
    const target = await this.docs.get(id);
    if (!target) return;
    await this.clearDefaults(id);
    const updated: TaskTemplate = { ...target, isDefault: true };
    await this.docs.update(id, updated, taskTemplateIndexAttributes(updated));
  }

  /**
   * Soft-deletes a template: fetches it, flags it deleted, and updates both the
   * S3 body and the pointer row so list queries filter it out.
   */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: TaskTemplate = { ...existing, deleted: true };
    await this.docs.update(id, deleted, taskTemplateIndexAttributes(deleted));
  }

  /**
   * Idempotently seeds the built-in default template when the collection is
   * empty. Returns the created template, or undefined when one already exists.
   */
  async ensureDefault(): Promise<TaskTemplate | undefined> {
    const summaries = await this.listSummaries();
    if (!needsDefaultSeed(summaries)) return undefined;
    return this.create(defaultTaskTemplateDraft());
  }

  /**
   * Clears the isDefault flag on every non-deleted template (optionally skipping
   * `exceptId`). Used to keep exactly one default.
   */
  private async clearDefaults(exceptId?: string): Promise<void> {
    const summaries = await this.listSummaries();
    const toClear = summaries.filter((s) => s.isDefault && s.id !== exceptId);
    await Promise.all(
      toClear.map(async (s) => {
        const existing = await this.docs.get(s.id);
        if (!existing) return;
        const cleared: TaskTemplate = { ...existing, isDefault: false };
        await this.docs.update(
          cleared.id,
          cleared,
          taskTemplateIndexAttributes(cleared),
        );
      }),
    );
  }

  /**
   * Builds a full TaskTemplate from a draft, seeding id/createdBy and preserving
   * createdAt on edit.
   */
  private fromDraft(
    id: string,
    draft: TaskTemplateDraft,
    createdBy: string,
    existingCreatedAt: string | undefined,
  ): TaskTemplate {
    const now = new Date().toISOString();
    return {
      id,
      name: draft.name.trim(),
      isDefault: draft.isDefault ?? false,
      content: draft.content,
      sections: (draft.sections ?? []).map((s) => ({ ...s })),
      createdBy,
      createdAt: existingCreatedAt ?? now,
      lastUpdated: now,
      deleted: false,
    };
  }
}
